import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Target,
  Building2,
  Users2,
  MessagesSquare,
  Calendar as CalendarIcon,
  TrendingUp,
  Sparkles,
  Send,
  Check,
  AlertTriangle,
  Star,
  Flame,
} from "lucide-react";
import {
  CLINIC_STAGES,
  VENDOR_STAGES,
  STAGE_LABEL,
  fmtDateTime,
  fmtMRR,
} from "./constants";
import { TaskRow } from "./TasksView";

export default function DashboardView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiResult, setAiResult] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/founder/dashboard");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const aiPrioritize = async () => {
    setAiBusy(true);
    setAiResult(null);
    try {
      const r = await api.post("/founder/ai/assist", {
        intent: "prioritize_overdue",
      });
      setAiResult(r.data?.text || "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAiBusy(false);
    }
  };

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (!data) return null;

  const k = data.kpis || {};

  return (
    <div className="space-y-6" data-testid="founder-os-dashboard">
      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        <Kpi label="Clinics contacted" value={k.clinics_contacted} />
        <Kpi label="Demos done" value={k.demos_completed} />
        <Kpi label="Pilots" value={k.pilot_clinics} />
        <Kpi label="Live" value={k.live_clinics} accent="emerald" />
        <Kpi label="Vendors approved" value={k.vendors_approved} />
        <Kpi label="Recurring wf" value={k.recurring_workflows} />
        <Kpi label="MRR" value={fmtMRR(k.mrr_cents)} accent="emerald" />
        <Kpi label="Active tasks" value={k.active_tasks} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Today's top priorities */}
        <Panel
          title="Today’s top priorities"
          icon={Star}
          accent="bg-amber-50 text-amber-700"
          testId="dashboard-top-priorities"
          empty={!data.top_priorities?.length && "No top priorities flagged. Star up to 5 tasks to focus."}
        >
          {data.top_priorities?.map((t) => (
            <TaskRow key={t.id} task={t} compact />
          ))}
        </Panel>

        {/* This week's goals */}
        <Panel
          title="This week"
          icon={Target}
          accent="bg-blue-50 text-blue-700"
          testId="dashboard-this-week"
          empty={!data.this_week_goals?.length && "Nothing scheduled this week."}
        >
          {data.this_week_goals?.map((t) => (
            <TaskRow key={t.id} task={t} compact />
          ))}
        </Panel>

        {/* Overdue */}
        <Panel
          title="Overdue"
          icon={AlertTriangle}
          accent="bg-rose-50 text-rose-700"
          testId="dashboard-overdue"
          headerExtra={
            <button
              type="button"
              onClick={aiPrioritize}
              disabled={aiBusy}
              data-testid="dashboard-ai-prioritize"
              className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white text-[10px] font-semibold px-2 py-1 hover:bg-slate-800 disabled:opacity-50"
            >
              <Sparkles className="w-3 h-3" /> {aiBusy ? "Thinking…" : "AI: pick 3"}
            </button>
          }
          empty={!data.overdue_tasks?.length && "Nothing overdue. Stay shipping."}
        >
          {data.overdue_tasks?.map((t) => (
            <TaskRow key={t.id} task={t} compact />
          ))}
          {aiResult && (
            <div
              className="mt-2 p-3 rounded-lg bg-slate-900 text-slate-100 text-xs whitespace-pre-wrap leading-relaxed"
              data-testid="dashboard-ai-result"
            >
              {aiResult}
            </div>
          )}
        </Panel>

        {/* Clinic pipeline counts */}
        <Panel
          title="Clinic pipeline"
          icon={Building2}
          accent="bg-violet-50 text-violet-700"
          testId="dashboard-clinic-pipeline"
        >
          <PipelineSparkline counts={data.clinic_pipeline_counts} stages={CLINIC_STAGES} />
        </Panel>

        {/* Vendor pipeline counts */}
        <Panel
          title="Vendor pipeline"
          icon={Users2}
          accent="bg-emerald-50 text-emerald-700"
          testId="dashboard-vendor-pipeline"
        >
          <PipelineSparkline counts={data.vendor_pipeline_counts} stages={VENDOR_STAGES} />
        </Panel>

        {/* Feedback queue */}
        <Panel
          title="Feedback queue"
          icon={MessagesSquare}
          accent="bg-sky-50 text-sky-700"
          testId="dashboard-feedback-queue"
          empty={!data.feedback_queue?.length && "Inbox zero on feedback."}
        >
          {data.feedback_queue?.map((f) => (
            <div
              key={f.id}
              className="flex items-start gap-2 py-1.5 text-xs border-b border-slate-100 last:border-0"
              data-testid={`dashboard-feedback-${f.id}`}
            >
              <span
                className={`inline-flex items-center justify-center w-1.5 h-1.5 rounded-full mt-1.5 ${
                  f.priority === "high"
                    ? "bg-rose-500"
                    : f.priority === "medium"
                    ? "bg-amber-500"
                    : "bg-slate-400"
                }`}
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">{f.title}</div>
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {(f.tags || []).map((t) => (
                    <span
                      key={t}
                      className={`text-[9px] uppercase tracking-wider rounded-full px-1.5 py-0.5 font-semibold ${
                        t === "workflow_friction"
                          ? "bg-rose-100 text-rose-700"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {t.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </Panel>

        {/* Upcoming meetings */}
        <Panel
          title="Upcoming"
          icon={CalendarIcon}
          accent="bg-indigo-50 text-indigo-700"
          testId="dashboard-upcoming-meetings"
          empty={!data.upcoming_meetings?.length && "No meetings in the next 7 days."}
        >
          {data.upcoming_meetings?.map((m) => (
            <div
              key={m.id}
              className="py-1.5 text-xs border-b border-slate-100 last:border-0"
              data-testid={`dashboard-meeting-${m.id}`}
            >
              <div className="font-medium text-slate-900 truncate">{m.title}</div>
              <div className="text-slate-500">
                {STAGE_LABEL(m.kind)} · {fmtDateTime(m.starts_at)}
              </div>
            </div>
          ))}
        </Panel>

        {/* Milestones */}
        <Panel
          title="Key milestones"
          icon={TrendingUp}
          accent="bg-emerald-50 text-emerald-700"
          testId="dashboard-milestones"
        >
          {(data.milestones || []).map((m, i) => (
            <div
              key={m.label || `milestone-${i}`}
              className="flex items-center gap-2 py-1.5 text-xs border-b border-slate-100 last:border-0"
            >
              <span
                className={`inline-flex w-4 h-4 rounded-full items-center justify-center ${
                  m.achieved ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"
                }`}
              >
                {m.achieved ? <Check className="w-2.5 h-2.5" /> : null}
              </span>
              <span className={m.achieved ? "text-slate-900 font-medium" : "text-slate-500"}>
                {m.label}
              </span>
            </div>
          ))}
        </Panel>

        {/* Hot leads — high-intent Opportunity Engine prospects with fresh signals */}
        <Panel
          title="Hot leads · 7d"
          icon={Flame}
          accent="bg-rose-50 text-rose-700"
          testId="dashboard-hot-leads"
          headerExtra={
            <a
              href="/admin/opportunity"
              className="text-[10px] font-semibold text-blue-700 hover:text-blue-900"
              data-testid="dashboard-hot-leads-link"
            >
              Open →
            </a>
          }
          empty={
            !data.hot_leads?.length &&
            "No high-intent prospects with fresh signals. Tag new signals in the Opportunity Engine to surface them here."
          }
        >
          {data.hot_leads?.map((p) => (
            <a
              key={p.id}
              href="/admin/opportunity"
              className="flex items-start gap-2 py-1.5 text-xs border-b border-slate-100 last:border-0 hover:bg-slate-50 -mx-1 px-1 rounded"
              data-testid={`dashboard-hot-lead-${p.id}`}
            >
              <span className="inline-flex w-7 h-5 rounded-full bg-rose-600 text-white items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">
                {p.intent_score}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-slate-900 truncate">{p.clinic_name}</div>
                <div className="text-[10px] text-slate-500 truncate">
                  {p.outreach_angle || p.top_signal || "—"}
                </div>
              </div>
            </a>
          ))}
        </Panel>

        {/* Daily digest preferences */}
        <DigestPanel />
      </div>
    </div>
  );
}

function DigestPanel() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/founder/digest-settings");
      setS(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = async (patch) => {
    setSaving(true);
    try {
      const r = await api.patch("/founder/digest-settings", patch);
      setS(r.data);
      toast.success("Digest preferences saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      await api.post("/founder/digest/send-now");
      toast.success("Digest sent — check your inbox.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Panel
      title="Daily digest"
      icon={Send}
      accent="bg-blue-50 text-blue-700"
      testId="dashboard-digest"
    >
      {loading || !s ? (
        <div className="text-xs text-slate-500 py-2">Loading…</div>
      ) : (
        <div className="space-y-2.5">
          <label className="flex items-center gap-2 text-xs select-none">
            <input
              type="checkbox"
              checked={s.enabled}
              disabled={saving}
              onChange={(e) => update({ enabled: e.target.checked })}
              data-testid="digest-enabled-toggle"
            />
            <span className="text-slate-700">
              Email me a daily founder digest at
            </span>
            <select
              value={s.send_hour_utc}
              disabled={saving}
              onChange={(e) => update({ send_hour_utc: Number(e.target.value) })}
              data-testid="digest-hour-select"
              className="rounded-lg border border-slate-200 px-2 py-1 text-xs"
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>
                  {String(h).padStart(2, "0")}:00 UTC
                </option>
              ))}
            </select>
          </label>
          <div className="text-[10px] text-slate-500 leading-snug">
            Sends to <span className="font-mono">{s.delivery_email}</span>. Skips automatically when there's nothing to report.
            {s.last_sent_at && (
              <> Last sent {fmtDateTime(s.last_sent_at)}.</>
            )}
          </div>
          <div className="pt-1.5 border-t border-slate-100">
            <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
              Delivery override
            </label>
            <div className="flex items-center gap-2">
              <input
                type="email"
                value={s.delivery_email_override || ""}
                placeholder={s.account_email || "kyle@noderahealth.com"}
                disabled={saving}
                onChange={(e) =>
                  setS({ ...s, delivery_email_override: e.target.value })
                }
                onBlur={(e) => {
                  const val = (e.target.value || "").trim();
                  if (val === (s.delivery_email_override || "")) return;
                  update({ delivery_email: val });
                }}
                data-testid="digest-delivery-email-input"
                className="flex-1 rounded-lg border border-slate-200 px-2 py-1 text-xs font-mono"
              />
              {s.delivery_email_override && (
                <button
                  type="button"
                  onClick={() => {
                    setS({ ...s, delivery_email_override: "" });
                    update({ delivery_email: "" });
                  }}
                  data-testid="digest-delivery-email-clear-btn"
                  className="text-[10px] text-slate-500 hover:text-slate-900 underline"
                >
                  Reset
                </button>
              )}
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              Leave blank to use the account email ({s.account_email}).
            </div>
          </div>
          <button
            type="button"
            onClick={sendNow}
            disabled={sending}
            data-testid="digest-send-now-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50"
          >
            <Send className="w-3 h-3" /> {sending ? "Sending…" : "Send digest now"}
          </button>
        </div>
      )}
    </Panel>
  );
}

function Kpi({ label, value, accent = "slate" }) {
  const ring = {
    slate: "border-slate-200 bg-white",
    emerald: "border-emerald-200 bg-emerald-50",
  }[accent];
  return (
    <div className={`rounded-xl border ${ring} p-3`} data-testid={`kpi-${label}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold truncate">
        {label}
      </div>
      <div className="font-display text-xl font-bold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}

function Panel({ title, icon: Icon, accent, testId, headerExtra, children, empty }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className={`inline-flex w-6 h-6 rounded-md items-center justify-center ${accent}`}>
            <Icon className="w-3.5 h-3.5" />
          </span>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700">
            {title}
          </div>
        </div>
        {headerExtra}
      </div>
      <div className="space-y-0.5">
        {empty ? (
          <div className="text-xs text-slate-400 py-3">{empty}</div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

function PipelineSparkline({ counts, stages }) {
  const total = stages.reduce((s, k) => s + (counts?.[k] || 0), 0);
  return (
    <div className="space-y-1.5">
      {stages.map((s) => {
        const v = counts?.[s] || 0;
        const pct = total ? (v / total) * 100 : 0;
        return (
          <div key={s} className="flex items-center gap-2 text-[11px]">
            <div className="w-28 text-slate-600 truncate">{STAGE_LABEL(s)}</div>
            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-slate-900"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="w-6 text-right font-semibold text-slate-900">{v}</div>
          </div>
        );
      })}
    </div>
  );
}
